/**
 * ProposalsBoardView — kanban view for CMB Proposals.
 *
 * V1 scope: visual only. Click a card → existing view modal in Proposals.tsx.
 * No drag-and-drop, no manual_stage pinning (CMB has simpler workflow).
 */

import { WORKFLOW_COLUMNS, groupProposalsByColumn, STALLED_THRESHOLD_DAYS } from './ProposalsBoardConfig';

type Row = Record<string, unknown>;

type ProposalSortKey =
  | 'date_desc' | 'date_asc'
  | 'amount_desc' | 'amount_asc'
  | 'customer_asc' | 'customer_desc'
  | 'reference_desc' | 'reference_asc';

interface ProposalsBoardViewProps {
  proposals: Row[];
  loading: boolean;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
  onView: (row: Row) => void;
  /** Per-column sort selection. Defaults to date_desc per column. */
  columnSorts?: Record<string, string>;
  /** Fired when the operator changes a column's sort dropdown. */
  onColumnSortChange?: (colId: string, sortType: string) => void;
}

function sortProposals(rows: Row[], sortKey: ProposalSortKey): Row[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sortKey) {
      case 'date_asc':
        return (new Date(a.created_at as string).getTime() || 0) - (new Date(b.created_at as string).getTime() || 0);
      case 'amount_desc':
        return (Number(b.total) || 0) - (Number(a.total) || 0);
      case 'amount_asc':
        return (Number(a.total) || 0) - (Number(b.total) || 0);
      case 'customer_asc':
        return ((a.customer_name as string) || '').localeCompare((b.customer_name as string) || '');
      case 'customer_desc':
        return ((b.customer_name as string) || '').localeCompare((a.customer_name as string) || '');
      case 'reference_desc':
        return ((b.reference_code as string) || '').localeCompare((a.reference_code as string) || '');
      case 'reference_asc':
        return ((a.reference_code as string) || '').localeCompare((b.reference_code as string) || '');
      case 'date_desc':
      default:
        return (new Date(b.created_at as string).getTime() || 0) - (new Date(a.created_at as string).getTime() || 0);
    }
  });
  return copy;
}

export default function ProposalsBoardView({
  proposals,
  loading,
  formatCurrency,
  formatDate,
  onView,
  columnSorts,
  onColumnSortChange,
}: ProposalsBoardViewProps) {
  if (loading) {
    return <div className="placeholder-page"><div className="spinner" /></div>;
  }

  const grouped = groupProposalsByColumn(proposals);

  return (
    <>
      <div
        style={{
          margin: '0.75rem 0 1rem',
          padding: '0.625rem 0.875rem',
          background: 'var(--color-bg)',
          border: '1px solid #e2e8f0',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-light)',
        }}
      >
        <strong>Workflow view (preview):</strong> stages auto-derive from status, last view, and conversion.
        Stalled threshold is {STALLED_THRESHOLD_DAYS} days without a customer view.
      </div>

      <div className="ops-board">
        {WORKFLOW_COLUMNS.map((col) => {
          const rawItems = grouped[col.id] || [];
          const sortKey = (columnSorts?.[col.id] || 'date_desc') as ProposalSortKey;
          const items = sortProposals(rawItems, sortKey);
          return (
            <div key={col.id} className="ops-board-column">
              <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
                <div className="ops-board-column-title-group">
                  <span className="ops-board-column-title">{col.label}</span>
                  <span className="ops-board-column-count">{items.length}</span>
                </div>
                {onColumnSortChange && (
                  <select
                    className="ops-board-column-sort"
                    value={sortKey}
                    onChange={(e) => onColumnSortChange(col.id, e.target.value)}
                  >
                    <optgroup label="Sort">
                      <option value="date_desc">Newest</option>
                      <option value="date_asc">Oldest</option>
                      <option value="amount_desc">Amount &darr;</option>
                      <option value="amount_asc">Amount &uarr;</option>
                      <option value="customer_asc">Customer A-Z</option>
                      <option value="customer_desc">Customer Z-A</option>
                      <option value="reference_desc">Reference &darr;</option>
                      <option value="reference_asc">Reference &uarr;</option>
                    </optgroup>
                  </select>
                )}
              </div>
              <div className="ops-board-column-body">
                {items.length === 0 ? (
                  <div className="ops-board-empty" title={col.helpText}>{col.helpText}</div>
                ) : (
                  items.slice(0, 30).map((p) => (
                    <ProposalCard
                      key={p.id as string}
                      proposal={p}
                      columnId={col.id}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      onView={onView}
                    />
                  ))
                )}
                {items.length > 30 && (
                  <div className="ops-board-more">+{items.length - 30} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface ProposalCardProps {
  proposal: Row;
  columnId: string;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string) => string;
  onView: (row: Row) => void;
}

function ProposalCard({ proposal, columnId, formatCurrency, formatDate, onView }: ProposalCardProps) {
  const total = Number(proposal.total) || 0;
  const customerName = (proposal.customer_name as string) || (proposal.customer_email as string) || 'Unnamed';
  const refCode = (proposal.reference_code as string) || '';

  return (
    <div className="ops-board-card" onClick={() => onView(proposal)}>
      <div className="ops-board-card-header">
        <span className="ops-board-card-customer" title={customerName}>{customerName}</span>
        <span className="ops-board-card-amount">{formatCurrency(total)}</span>
      </div>
      <div className="ops-board-card-meta flex items-center justify-between gap-sm">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', minWidth: 0 }}>
          {refCode && <code className="text-xs text-muted">{refCode}</code>}
        </div>
      </div>
      <div className="ops-board-card-meta mt-xs text-xs">
        <ActivityLine proposal={proposal} columnId={columnId} formatDate={formatDate} />
      </div>
    </div>
  );
}

interface ActivityLineProps {
  proposal: Row;
  columnId: string;
  formatDate: (dateStr: string) => string;
}

function ActivityLine({ proposal, columnId, formatDate }: ActivityLineProps) {
  const now = Date.now();
  const daysSince = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    return Math.floor((now - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  const sentAgo = daysSince(proposal.sent_at as string | null);
  const viewedAgo = daysSince(proposal.viewed_at as string | null);
  const expiresIn = proposal.expires_at
    ? Math.ceil((new Date(proposal.expires_at as string).getTime() - now) / (1000 * 60 * 60 * 24))
    : null;

  if (columnId === 'drafting') {
    return <span>Created {formatDate(proposal.created_at as string)}</span>;
  }
  if (columnId === 'sent') {
    return <span>Sent {sentAgo !== null ? `${sentAgo}d ago` : 'recently'} · not opened</span>;
  }
  if (columnId === 'engaged') {
    return (
      <span>
        Viewed {viewedAgo !== null ? `${viewedAgo}d ago` : 'recently'}
        {expiresIn !== null && expiresIn > 0 && expiresIn <= 7 && (
          <span style={{ color: 'var(--color-warning)', marginLeft: '0.5rem' }}>· expires in {expiresIn}d</span>
        )}
      </span>
    );
  }
  if (columnId === 'stalled') {
    return (
      <span className="text-error">
        Last viewed {viewedAgo !== null ? `${viewedAgo}d ago` : 'a while ago'}
      </span>
    );
  }
  if (columnId === 'won') {
    return <span className="text-success">Converted to booking</span>;
  }
  if (columnId === 'lost') {
    return <span>Expired {formatDate(proposal.expires_at as string)}</span>;
  }
  return <span>{formatDate(proposal.created_at as string)}</span>;
}
